import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import styles from './Sidebar.module.css';
import { useTheme } from '../../../../context/ThemeContext';
import { adminNavItems, adminNavIcons, adminPageItems } from './navConfig';
import type { SectionKey } from './navConfig';

export interface SidebarProps {
  collapsed: boolean;
  activeSection: SectionKey;
  availableSections?: SectionKey[];
  allowedFeatures?: string[] | null; // null means full access (default admin)
  onToggleCollapse: () => void;
  onNavClick: (section: SectionKey) => void;
  onProfileClick: () => void;
  onLogout: () => void;
}

const pageFeatureMap: Partial<Record<SectionKey, string>> = {
  overview: 'dashboard',
  regulations: 'dashboard',
  trending: 'dashboard',
  charts: 'dashboard',
  newsletters: 'dashboard',
  search: 'reg_search',
  'search-history': 'reg_search',
  chat: 'ai_chat',
  'chat-history': 'ai_chat',
};

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  activeSection,
  availableSections,
  allowedFeatures,
  onToggleCollapse,
  onNavClick,
  onProfileClick,
  onLogout,
}) => {
  const { theme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileNav = (section: SectionKey) => {
    onNavClick(section);
    setMobileMenuOpen(false);
  };

  const visibleNavItems = availableSections?.length
    ? adminNavItems.filter((item) => availableSections.includes(item.key))
    : adminNavItems;

  const visiblePageLinks = adminPageItems
    .filter((item) => !availableSections?.length || availableSections.includes(item.key))
    .filter((item) => {
      const requiredFeature = pageFeatureMap[item.key];
      return allowedFeatures === null || allowedFeatures === undefined || !requiredFeature || allowedFeatures.includes(requiredFeature);
    });

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <aside className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}>
        <div className={styles.brandHeader}>
          <div className={styles.brandBlock}>
            {!collapsed && (
                <img
                  src="/Policy Ora Final Logo.png"
                  alt="Policyora logo"
                  className={`${styles.brandLogo} ${theme === 'dark' ? styles.brandLogoDark : styles.brandLogoLight}`}
                />
              )}
          </div>
          {collapsed ? (
            <button className={styles.collapseToggle} onClick={onToggleCollapse} aria-label="Expand sidebar" title="Expand sidebar"><Menu size={18} aria-hidden /></button>
          ) : (
            <button className={styles.collapseToggle} onClick={onToggleCollapse} aria-label="Collapse sidebar">✕</button>
          )}
        </div>

        <nav className={styles.sidebarNav}>
          {visibleNavItems.map(item => (
            <button
              key={item.key}
              type="button"
              className={`${styles.sidebarNavItem} ${styles[`navItem_${item.key}`] || ''} ${activeSection === item.key ? styles.sidebarNavItemActive : ''}`}
              onClick={() => onNavClick(item.key)}
              title={collapsed ? item.label : undefined}
            >
              <span className={styles.navText}>
                <span className={styles.navIcon}>{adminNavIcons[item.key]}</span>
                {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
              </span>
            </button>
          ))}
        </nav>

        <div className={styles.sidebarPagesSection}>
          {!collapsed && <div className={styles.pagesSectionLabel}>Pages</div>}
          {visiblePageLinks.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.sidebarNavItem} ${activeSection === key ? styles.sidebarNavItemActive : ''}`}
              onClick={() => onNavClick(key)}
              title={collapsed ? label : undefined}
            >
              <span className={styles.navText}>
                <span className={styles.navIcon}>{adminNavIcons[key]}</span>
                {!collapsed && <span className={styles.navLabel}>{label}</span>}
              </span>
            </button>
          ))}
        </div>

        <div className={`${styles.sidebarFooter} ${collapsed ? styles.sidebarFooterCollapsed : ''}`}>
          <button className={styles.ghostButton} onClick={onProfileClick}>View profile</button>
          <button className={styles.dangerButton} onClick={onLogout}>Log out</button>
        </div>
      </aside>

      {/* ── Mobile top bar ── */}
      <div className={styles.mobileBar}>
        <button className={styles.mobileHamburger} onClick={() => setMobileMenuOpen(o => !o)} aria-label="Open menu">
          {mobileMenuOpen ? '✕' : <Menu size={18} aria-hidden />}
        </button>
        <div className={styles.mobileBarPages}>
          {visiblePageLinks.map(({ key, label }) => (
            <button key={key} type="button" className={styles.mobilePageBtn} onClick={() => handleMobileNav(key)} title={label}>
              {adminNavIcons[key]}
            </button>
          ))}
        </div>
        <div className={styles.mobileBarFooter}>
          <button className={styles.ghostButton} onClick={onProfileClick}>View profile</button>
          <button className={styles.dangerButton} onClick={onLogout}>Log out</button>
        </div>
      </div>

      {/* ── Mobile dropdown menu ── */}
      {mobileMenuOpen && (
        <>
          <div className={styles.mobileOverlay} onClick={() => setMobileMenuOpen(false)} />
          <div className={styles.mobileDropdown}>
            <div className={styles.mobileDropdownSection}>Admin</div>
            {visibleNavItems.map(item => (
              <button
                key={item.key}
                type="button"
                className={`${styles.mobileDropdownItem} ${activeSection === item.key ? styles.mobileDropdownItemActive : ''}`}
                onClick={() => handleMobileNav(item.key)}
              >
                <span className={styles.navIcon}>{adminNavIcons[item.key]}</span>
                <span>{item.label}</span>
              </button>
            ))}
            <div className={styles.mobileDropdownSection}>Pages</div>
            {visiblePageLinks.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`${styles.mobileDropdownItem} ${activeSection === key ? styles.mobileDropdownItemActive : ''}`}
                onClick={() => handleMobileNav(key)}
              >
                <span className={styles.navIcon}>{adminNavIcons[key]}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
};
